// src/admin/pages/Costs/BusinessCostsPage.jsx

import React, { useEffect, useState } from "react";
import { fetchRestaurants } from "./restaurantApi";
import OverheadCostsList from "./OverheadCostsList";
import ExtraRevenuesList from "./ExtraRevenuesList";

/**
 * BusinessCostsPage
 * - Nessun overflow orizzontale su dispositivi mobili
 * - Card dei filtri full-width, senza maxWidth
 * - Label e select in colonna, pulsanti tab reattivi
 * - Design pulito e user-friendly
 */

const palette = {
  primary: "#FF8C00",
  primaryDark: "#FF6600",
  background: "#FAFAFA",
  card: "#FFFFFF",
  textDark: "#333333",
  textLight: "#FFFFFF",
  border: "#DDDDDD",
};

const styles = {
  container: {
    backgroundColor: palette.background,
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    // Padding orizzontale leggero, per non rischiare overflow
    padding: "16px 8px",
    boxSizing: "border-box",
    // Spazio extra per dispositivi con notch
    paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0))",
    overflowX: "hidden", // evitiamo scroll orizzontale
  },
  headerTitle: {
    color: palette.textDark,
    fontSize: "22px",
    marginBottom: "16px",
    textAlign: "center",
    fontWeight: "bold",
  },
  filterCard: {
    backgroundColor: palette.card,
    borderRadius: "8px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
    padding: "16px",
    width: "100%",           // full-width
    boxSizing: "border-box", // importate per evitare overflow
    marginBottom: "20px",
  },
  filterRow: {
    display: "flex",
    flexDirection: "column", // in colonna per evitare sforamenti in orizzontale
    gap: "6px",
    marginBottom: "12px",
  },
  label: {
    fontSize: "14px",
    color: palette.textDark,
  },
  select: {
    width: "100%",
    padding: "10px",
    borderRadius: "6px",
    border: `1px solid ${palette.border}`,
    fontSize: "14px",
    cursor: "pointer",
    boxSizing: "border-box",
  },
  tabContainer: {
    display: "flex",
    borderRadius: "6px",
    overflow: "hidden",
    marginTop: "8px",
    border: `1px solid ${palette.border}`,
    // Forziamo flex-wrap se lo schermo è minuscolo
    flexWrap: "wrap",
  },
  tabButton: {
    flex: "1 0 50%", // almeno metà schermo su dispositivi stretti
    padding: "12px",
    fontSize: "15px",
    border: "none",
    cursor: "pointer",
    backgroundColor: palette.card,
    color: palette.textDark,
    transition: "background-color 0.3s, color 0.3s",
    textAlign: "center",
  },
  tabButtonActive: {
    backgroundColor: palette.primary,
    color: palette.textLight,
  },
};

export default function BusinessCostsPage() {
  const [activeTab, setActiveTab] = useState("costs");
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchRestaurants();
        setRestaurants(data || []);
        if (data && data.length > 0) {
          setSelectedRestaurant(data[0].id);
        }
      } catch (err) {
        console.error("Errore fetchRestaurants:", err);
      }
    })();
  }, []);

  const handleSelectRestaurant = (event) => {
    const val = parseInt(event.target.value, 10);
    setSelectedRestaurant(val);
  };

  const getTabStyle = (tabName) => {
    const baseStyle = { ...styles.tabButton };
    if (activeTab === tabName) {
      Object.assign(baseStyle, styles.tabButtonActive);
    }
    return baseStyle;
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.headerTitle}>Gestione Costi &amp; Ricavi Extra</h1>

      <div style={styles.filterCard}>
        <div style={styles.filterRow}>
          <label style={styles.label}>Ristorante:</label>
          <select
            style={styles.select}
            value={selectedRestaurant || ""}
            onChange={handleSelectRestaurant}
          >
            {restaurants.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.tabContainer}>
          <button style={getTabStyle("costs")} onClick={() => setActiveTab("costs")}>
            Costi
          </button>
          <button style={getTabStyle("revenues")} onClick={() => setActiveTab("revenues")}>
            Ricavi
          </button>
        </div>
      </div>

      {activeTab === "costs" && (
        <OverheadCostsList restaurantId={selectedRestaurant} />
      )}
      {activeTab === "revenues" && (
        <ExtraRevenuesList restaurantId={selectedRestaurant} />
      )}
    </div>
  );
}
